<template>
    <div class="dropfile-main-container">
        <div v-if="file && !typeError" class="file-content">
            <p>{{ file.name }}</p>
            <i class="fa-solid fa-circle-xmark" @click="clearPicture"></i>
        </div>
        <div class="dropfile-drop-zone" v-else>
            <div
                class="drop-zone-wrapper"
                @dragover="preventDefault"
                @dragleave="preventDefault"
                @drop="fileDroping"
            >
                <input
                    ref="file-selection"
                    id="file-selection"
                    type="file"
                    @change="onFileChanged"
                    :accept="acceptedAttribute"
                    capture
                />
                <label for="file-selection" class="drop-zone-label">
                    <img
                        :src="require('@/assets/images/icons/download.svg')"
                        alt="Download"
                    />
                    <div class="file-selection-text">
                        {{ $t("flyers.readme_first") }}
                        <span @click="openImportDialog">
                            {{ $t("flyers.readme_second") }}
                        </span>
                        {{ $t("flyers.readme_last") }}
                    </div>
                    <div class="file-selection-text">
                        {{
                            $t("amazonKey.details.form.authorizedFiles", {
                                types: typesMessage,
                                size: maxSize,
                            })
                        }}
                    </div>
                </label>
            </div>
            <p v-if="checkErrors">{{ checkErrors }}</p>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        allowedTypes: {
            type: Array,
            required: true,
            default: () => [],
        },
        maxSize: {
            type: Number,
            required: true,
            default: 2,
        },
    },

    emits: ["changeFile"],

    data() {
        return {
            file: null,
        }
    },

    computed: {
        typesMessage() {
            return this.formatTypesManual(this.allowedTypes)
        },

        // Define all types allowed when finder is open
        acceptedAttribute() {
            return this.allowedTypes.map((item) => `.${item}`).join(", ")
        },

        // Check if file contains differents errors
        checkErrors() {
            let message = null

            if (this.file) {
                // Exctract type of file
                const fileType = this.file.type.split("/").pop().toLowerCase()

                // Convert file size in MB
                const fileSize = this.file.size / 1000000

                // Check if type is accepted
                if (!this.allowedTypes.includes(fileType)) {
                    message = this.$t("flyers.error_file_not_supported")
                }
                // Check if file size isn't more bigger than limit
                else if (this.maxSize < fileSize) {
                    message = this.$t("amazonKey.details.form.errors.maxSize", {
                        size: this.maxSize,
                    })
                }
            }

            return message
        },
    },

    methods: {
        // Format error message according to the number of formats allowed
        formatTypesManual(list) {
            switch (list.length) {
                // If there is only 1 element, return only that item
                case 1:
                    return list[0].toUpperCase()

                // If there is 2 items, return them with a separator like "or"
                case 2:
                    return list
                        .map((item) => item.toUpperCase())
                        .join(` ${this.$t("flyers.divider").toLowerCase()} `)

                // If there is more than 2 items, return them with a separator like "," and add "or" at the end
                default: {
                    let up = list.map((item) => item.toUpperCase())
                    return (
                        up.slice(0, -1).join(", ") +
                        ` ${this.$t("flyers.divider").toLowerCase()} ` +
                        up.at(-1)
                    )
                }
            }
        },

        fileDroping(e) {
            e.preventDefault()
            this.file = e.dataTransfer.files[0]

            this.$emit("changeFile", this.file)
        },

        onFileChanged(e) {
            if (e.target.files || e.dataTransfer.files) {
                this.file = e.target.files[0] || e.dataTransfer.files[0]

                this.$emit("changeFile", this.file)
            }
        },

        preventDefault(e) {
            e.preventDefault()
        },

        clearPicture() {
            this.file = null
            this.$emit("changeFile", this.file)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global.scss";
@import "@/assets/scss/variables/fontSize.scss";
h1,
p {
    margin: 0;
}

.dropfile-main-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;

    input[type="file"] {
        position: absolute;
        opacity: 0;
        overflow: hidden;
        width: 1px;
        height: 1px;
    }

    .dropfile-drop-zone {
        width: 100%;
        .drop-zone-wrapper {
            width: 100%;
            .drop-zone-label {
                display: flex;
                width: 100%;
                height: 100%;
                padding: 1em 2em;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                border: 2px dashed $blue-neutral;
                gap: 2px;
                border-radius: 10px;

                @media all and (max-width: 834px) {
                    width: auto;
                }

                .file-selection-text {
                    text-align: center;
                }

                img {
                    margin-bottom: 12px;
                }

                span {
                    color: $orange-neutral;
                    text-decoration: underline;
                    cursor: pointer;
                    -webkit-user-select: none;
                    -ms-user-select: none;
                    user-select: none;
                }
            }
        }

        .summary {
            display: flex;
            flex-direction: column;
            padding: 15px;
            border-radius: 5px;
            background-color: $blue-pale;

            .card-import {
                display: flex;
                flex-direction: column;
                gap: 5px;

                .infos {
                    display: flex;
                    align-items: center;
                    align-self: stretch;
                    gap: 10px;

                    .header {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: flex-start;
                        flex: 1 0 0;

                        .file-properties {
                            color: $blue-neutral;
                            font-family: $font-avenir-book;
                        }
                    }

                    .cross-wrapper {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        gap: 0.5em;
                        cursor: pointer;
                    }
                }

                .description {
                    display: flex;
                    align-items: flex-start;
                    gap: 5px;
                    margin-left: 40px;

                    .valid-loading {
                        animation: rotation 1s linear infinite;
                    }

                    .description-success {
                        .success {
                            font-size: 14px;
                        }

                        .next {
                            font-weight: 350;
                            font-family: $font-avenir-book;
                        }
                    }
                }
            }
        }
    }

    .file-content {
        display: flex;
        align-items: center;
        gap: 0.5em;

        i {
            cursor: pointer;
            transition: all 0.1s ease-in-out;

            &:hover {
                transform: scale(1.2);
                transition: all 0.1s ease-in-out;
            }
        }
    }
}
</style>
